import useFetch from '../../../hooks/useFetch';
import { useState } from 'react';
import { buildEndpoint } from '../EndPoints';
import { ServiceEvents } from '../shared/interfaces';
import { LOCAL_STORAGE } from '../../../config';
import { UserDataForm, UserPasswordDataForm } from '../../models/User';

type Credentials = {
    username: string;
    password: string;
};

const useAuthService = () => {
    const { doGet, doPost, doPut } = useFetch();

    const [fetchingLoginAuth, setFetchingLoginAuth] = useState(false);
    const [fetchingVerifyEmail, setFetchingVerifyEmail] = useState(false);
    const [fetchingResendVerification, setFetchingResendVerification] = useState(false);
    const [fetchingLogoutAuth, setFetchingLogoutAuth] = useState(false);
    const [fetchingValidateTokenAuth, setFetchingValidateTokenAuth] = useState(false);
    const [fetchingGetRoles, setFetchingGetRoles] = useState(false);
    const [fetchingSendCodeRecoveryPassword, setFetchingSendCodeRecoveryPassword] = useState(false);
    const [fetchingChangePasswordByCode, setFetchingChangePasswordByCode] = useState(false);
    const [fetchingGetProfile, setFetchingGetProfile] = useState(false);
    const [fetchingUpdateProfile, setFetchingUpdateProfile] = useState(false);
    const [fetchingUpdateProfilePassword, setFetchingUpdateProfilePassword] = useState(false);
    const [fetchingActivateAccount, setFetchingActivateAccount] = useState(false);
    const [fetchingValidateToken, setFetchingValidateToken] = useState(false);

    const BASE_PATH = 'auths';

    const loginAuth = (credentials: Credentials, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            headers: {
                Authorization: ''
            },
            url: buildEndpoint(`${BASE_PATH}/login`),
            body: credentials,
            setFetching: setFetchingLoginAuth
        });
    };

    const logoutAuth = (events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/logout`),
            setFetching: setFetchingLogoutAuth
        });
    };

    const validateTokenAuth = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/validate-token`),
            setFetching: setFetchingValidateTokenAuth
        });
    };

    const getRoles = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/get-roles`),
            setFetching: setFetchingGetRoles
        });
    };

    // Activa la cuenta con el enlace del correo de verificación (inicia sesión)
    const verifyEmail = (token: string, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/verify-email`),
            headers: { Authorization: '' },
            body: { token },
            setFetching: setFetchingVerifyEmail
        });
    };

    const resendVerification = (email: string, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/resend-verification`),
            headers: { Authorization: '' },
            body: { email },
            setFetching: setFetchingResendVerification
        });
    };

    const sendCodeRecoveryPassword = (email: string, events: ServiceEvents = {}) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/send-code-recovery-password`),
            headers: {
                Authorization: ''
            },
            body: { email: email },
            setFetching: setFetchingSendCodeRecoveryPassword
        });
    };

    const changePasswordByCode = (
        email: string,
        code: string,
        password: string,
        repeat_password: string,
        events: ServiceEvents = {}
    ) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/change-password-by-code`),
            headers: {
                Authorization: ''
            },
            body: {
                email,
                code_recovery_password: code,
                password: password,
                repeat_password: repeat_password
            },
            setFetching: setFetchingChangePasswordByCode
        });
    };

    const getProfile = (events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/get-profile`),
            setFetching: setFetchingGetProfile
        });
    };

    const updateProfile = (userProfile: UserDataForm, events: ServiceEvents = {}) => {
        doPut({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/update-profile`),
            body: userProfile,
            setFetching: setFetchingUpdateProfile
        });
    };

    const updateProfilePassword = (
        userProfile: UserPasswordDataForm,
        events: ServiceEvents = {}
    ) => {
        doPut({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/update-profile-password`),
            body: userProfile,
            setFetching: setFetchingUpdateProfilePassword
        });
    };

    const validateActivationToken = (token: string, events: ServiceEvents = {}) => {
        doGet({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/validate-activation-token?token=${token}`),
            headers: {
                Authorization: ''
            },
            setFetching: setFetchingValidateToken
        });
    };

    const activateAccount = (
        token: string,
        password: string,
        confirmPassword: string,
        events: ServiceEvents = {}
    ) => {
        doPost({
            ...events,
            url: buildEndpoint(`${BASE_PATH}/activate-account`),
            headers: {
                Authorization: ''
            },
            body: {
                token: token,
                password: password,
                confirm_password: confirmPassword
            },
            setFetching: setFetchingActivateAccount
        });
    };

    return {
        loginAuth,
        logoutAuth,
        validateTokenAuth,
        getRoles,
        sendCodeRecoveryPassword,
        changePasswordByCode,
        verifyEmail,
        resendVerification,
        fetchingVerifyEmail,
        fetchingResendVerification,
        validateActivationToken,
        activateAccount,
        fetchingGetProfile,
        fetchingUpdateProfile,
        fetchingChangePasswordByCode,
        fetchingSendCodeRecoveryPassword,
        fetchingLoginAuth,
        fetchingGetRoles,
        fetchingLogoutAuth,
        fetchingValidateTokenAuth,
        fetchingUpdateProfilePassword,
        fetchingActivateAccount,
        fetchingValidateToken,
        getProfile,
        updateProfile,
        updateProfilePassword
    };
};

export default useAuthService;
